// Regras PURAS da tela de Leilões (H3 PO1-3084): contagem regressiva pela hora
// do SERVIDOR, ordenação dos lances pela regra de vitória, justificativa fora
// do menor, rótulos de estado e formatação. Sem React, sem rede: testável com
// Jest puro (Leiloes/__tests__/regras.test.js).
//
// Contagem regressiva: toda resposta do backend traz `serverNow`. Guardamos a
// diferença entre o relógio do servidor e o do navegador no momento da resposta
// e aplicamos nos ticks seguintes — o navegador pode estar com a hora errada,
// e é a hora do servidor que fecha a janela.

export const ESTADO_UI = {
  aberta: { label: "Aberta", tone: "info" },
  encerrada: { label: "Aguardando escolha", tone: "warning" },
  adjudicada: { label: "Adjudicada", tone: "success" },
  deserta: { label: "Deserta", tone: "neutral" },
  cancelada: { label: "Cancelada", tone: "neutral" },
};

export const ESTADOS_ORDEM = ["aberta", "encerrada", "adjudicada", "deserta", "cancelada"];

export const CONVIDADO_UI = {
  convidado: { label: "Convidado", tone: "neutral" },
  visualizou: { label: "Abriu o link", tone: "info" },
  respondeu: { label: "Deu lance", tone: "success" },
  recusou: { label: "Recusou", tone: "warning" },
};

export const DURACAO = { padrao: 30, min: 15, max: 1440, presets: [15, 30, 60, 120, 240, 1440] };
export const JUSTIFICATIVA_MIN = 10;
export const PRAZO = { min: 1, max: 90 };
export const STATUS_LEILOAVEL = ["Pendente", "Stand-by"];

export function podeLeiloar(emb) {
  if (!emb) return false;
  if (emb.isLatestVersion === false) return false;
  return STATUS_LEILOAVEL.indexOf(emb.statusEmbarque) !== -1;
}

export function ts(v) {
  if (!v) return 0;
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : 0;
}

// Diferença servidor − navegador (ms) no instante em que a resposta chegou.
export function offsetDoServidor(serverNow, clientNow) {
  const s = ts(serverNow);
  if (!s) return 0;
  return s - (clientNow === undefined ? Date.now() : clientNow);
}

// "Agora" pelo relógio do servidor.
export function agoraServidor(offsetMs, clientNow) {
  return (clientNow === undefined ? Date.now() : clientNow) + (offsetMs || 0);
}

export function restanteMs(fechaEm, offsetMs, clientNow) {
  return ts(fechaEm) - agoraServidor(offsetMs, clientNow);
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

// "1 h 05 min", "12 min 09 s", "45 s", "Encerrado".
export function formatarRestante(ms) {
  if (!(ms > 0)) return "Encerrado";
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h} h ${pad2(m)} min`;
  if (m > 0) return `${m} min ${pad2(s)} s`;
  return `${s} s`;
}

// Nível de urgência para a cor do countdown.
export function nivelRestante(ms) {
  if (!(ms > 0)) return "danger";
  if (ms <= 5 * 60 * 1000) return "danger";
  if (ms <= 15 * 60 * 1000) return "warning";
  return "success";
}

export function countdown(fechaEm, offsetMs, clientNow) {
  const ms = restanteMs(fechaEm, offsetMs, clientNow);
  return { ms, texto: formatarRestante(ms), level: nivelRestante(ms) };
}

// Lances vigentes ordenados pela regra de vitória (menor valor; empate = o
// que chegou primeiro). Aceita a lista já marcada pelo backend (`vigente`) ou
// a lista crua (então deriva o último de cada transportadora).
export function ordenarVigentes(lances) {
  const lista = Array.isArray(lances) ? lances : [];
  const marcados = lista.some((l) => l && typeof l.vigente === "boolean");
  let vigentes;
  if (marcados) {
    vigentes = lista.filter((l) => l && l.vigente);
  } else {
    const por = {};
    lista.forEach((l) => {
      if (!l) return;
      const atual = por[l.idTransportadora];
      if (!atual || ts(l.criadoEm) >= ts(atual.criadoEm)) por[l.idTransportadora] = l;
    });
    vigentes = Object.keys(por).map((k) => por[k]);
  }
  return vigentes.slice().sort((a, b) => (a.valor !== b.valor ? a.valor - b.valor : ts(a.criadoEm) - ts(b.criadoEm)));
}

export function menorLanceId(lances) {
  const v = ordenarVigentes(lances);
  return v.length ? String(v[0]._id) : null;
}

export function precisaJustificar(leilao, lanceId) {
  const menor = (leilao && leilao.menorLanceId) || menorLanceId(leilao && leilao.lances);
  if (!menor || !lanceId) return false;
  return String(menor) !== String(lanceId);
}

export function justificativaValida(texto) {
  return typeof texto === "string" && texto.trim().length >= JUSTIFICATIVA_MIN;
}

// Formatação manual (sem toLocaleString): o Node 12 do Jest/Amplify vem sem
// ICU completo e "pt-BR" vira en-US em silêncio.
export function fmtNumeroBR(n, casas = 2) {
  const fixo = Math.abs(Number(n)).toFixed(casas);
  const partes = fixo.split(".");
  const inteiro = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${Number(n) < 0 ? "-" : ""}${inteiro}${casas > 0 ? `,${partes[1]}` : ""}`;
}

export function fmtBRL(v) {
  if (v === null || v === undefined || v === "" || !Number.isFinite(Number(v))) return "—";
  return `R$ ${fmtNumeroBR(v, 2)}`;
}

export function fmtDataHora(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (!Number.isFinite(d.getTime())) return "—";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export function fmtData(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (!Number.isFinite(d.getTime())) return "—";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

// Máscara de moeda da página pública/Spot: digita só dígitos, vira centavos.
export function parseBRL(masked) {
  if (masked === null || masked === undefined || masked === "") return null;
  if (typeof masked === "number") return masked;
  const digits = String(masked).replace(/\D/g, "");
  if (!digits) return null;
  return Number(digits) / 100;
}

export function formatBRLInput(value) {
  const num = parseBRL(value);
  if (num === null) return "";
  return fmtNumeroBR(num, 2);
}

// 'YYYY-MM-DD' local de hoje (valor mínimo do input de coleta).
export function hojeISO(d) {
  const base = d || new Date();
  return `${base.getFullYear()}-${pad2(base.getMonth() + 1)}-${pad2(base.getDate())}`;
}

export function duracaoValida(min) {
  const n = Number(min);
  return Number.isInteger(n) && n >= DURACAO.min && n <= DURACAO.max;
}

export function cidadeUf(local) {
  if (!local) return "—";
  return [local.cidade, local.uf].filter(Boolean).join("/") || "—";
}

// Filtro de texto da listagem: nº, cotação, destino, transportadora vencedora.
export function matchesSearch(item, termo) {
  if (!termo) return true;
  const alvo = String(termo).toLowerCase();
  const campos = [
    item.numero != null ? `#${item.numero}` : null,
    item.numero != null ? String(item.numero) : null,
    item.numeroCotacao != null ? String(item.numeroCotacao) : null,
    item.baseNumEmbarque,
    item.carga && item.carga.destino && item.carga.destino.cidade,
    item.carga && item.carga.destino && item.carga.destino.uf,
    item.vencedor && item.vencedor.nome,
  ];
  return campos.some((c) => c && String(c).toLowerCase().indexOf(alvo) !== -1);
}

export function resumoConvidados(leilao) {
  const c = (leilao && leilao.convidados) || [];
  return {
    total: c.length,
    abriram: c.filter((x) => x.estado === "visualizou" || x.estado === "respondeu").length,
    responderam: c.filter((x) => x.estado === "respondeu").length,
    recusaram: c.filter((x) => x.estado === "recusou").length,
  };
}

// ── Detalhe do leilão (revisão do QA de 13/10/2026) ─────────────────────────

// Cor do estado por token (barra do banner e textos).
export const TONE_VAR = {
  info: "var(--everlog-status-info)",
  warning: "var(--everlog-status-warning)",
  success: "var(--everlog-status-success)",
  danger: "var(--everlog-status-danger)",
  neutral: "var(--everlog-status-neutral)",
};

// "08:39" (hora local do navegador).
export function fmtHora(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (!Number.isFinite(d.getTime())) return "—";
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

// 15 → "15 min"; 90 → "1 h 30 min"; 1440 → "24 h".
export function fmtDuracao(min) {
  const n = Number(min);
  if (!Number.isFinite(n) || n <= 0) return "—";
  const h = Math.floor(n / 60);
  const m = n % 60;
  if (!h) return `${m} min`;
  return m ? `${h} h ${pad2(m)} min` : `${h} h`;
}

export function fmtNumero(v, casas = 0) {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return "—";
  return fmtNumeroBR(v, casas);
}

// 0.0456 → "4,6%".
export function fmtPct(fracao) {
  if (fracao === null || fracao === undefined || !Number.isFinite(Number(fracao))) return "—";
  return `${fmtNumeroBR(Number(fracao) * 100, 1)}%`;
}

// (valor − alvo) / alvo; negativo = abaixo do alvo.
export function diferencaPct(valor, alvo) {
  const v = Number(valor);
  const a = Number(alvo);
  if (!Number.isFinite(v) || !(a > 0)) return null;
  return (v - a) / a;
}

// Fração da janela já consumida (0..1), pela hora do servidor.
export function progressoJanela(abertaEm, fechaEm, offsetMs, clientNow) {
  const ini = ts(abertaEm);
  const fim = ts(fechaEm);
  if (!ini || !(fim > ini)) return 1;
  const agora = agoraServidor(offsetMs, clientNow);
  return Math.min(1, Math.max(0, (agora - ini) / (fim - ini)));
}

// ── Novo leilão (revisto em 13/10/2026) ─────────────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function emailValido(v) {
  return EMAIL_RE.test(String(v || "").trim());
}

export function temEmailCotacao(c) {
  return Boolean(c && c.emails && c.emails.length);
}

// Cobertura de e-mail de Cotação: o alerta do modal (a maioria das
// transportadoras costuma estar sem contato de Cotação).
export function coberturaEmail(candidatos) {
  const lista = candidatos || [];
  const carteira = lista.filter((c) => c.naCarteira);
  return {
    total: lista.length,
    semEmail: lista.filter((c) => !temEmailCotacao(c)).length,
    carteira: carteira.length,
    carteiraSemEmail: carteira.filter((c) => !temEmailCotacao(c)).length,
  };
}

function semAcento(s) {
  return String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

export const FILTRO_CANDIDATO = { todas: "todas", carteira: "carteira", comEmail: "comEmail", semEmail: "semEmail" };

// Busca por nome, CNPJ ou e-mail + filtro rápido.
export function filtrarCandidatos(candidatos, termo, filtro) {
  const t = semAcento(termo).trim();
  const digitos = String(termo || "").replace(/\D/g, "");
  return (candidatos || []).filter((c) => {
    if (filtro === FILTRO_CANDIDATO.carteira && !c.naCarteira) return false;
    if (filtro === FILTRO_CANDIDATO.comEmail && !temEmailCotacao(c)) return false;
    if (filtro === FILTRO_CANDIDATO.semEmail && temEmailCotacao(c)) return false;
    if (!t) return true;
    if (semAcento(c.nome).indexOf(t) !== -1) return true;
    if (digitos.length >= 3 && String(c.cnpj || "").replace(/\D/g, "").indexOf(digitos) !== -1) return true;
    return (c.emails || []).some((e) => semAcento(e).indexOf(t) !== -1);
  });
}

// PO1-3648 (épico PO1-3359): candidato que veio da Rede Everlog pela cobertura
// DECLARADA (não por CT-e). O leilão manda origem 'rede'/fonte 'rede'; o BID,
// origem 'REDE'. Não entra na pré-seleção nem conta como "atende o destino".
export function ehCandidatoRede(c) {
  if (!c) return false;
  const o = String(c.origem || "").toLowerCase();
  const f = String(c.fonte || "").toLowerCase();
  return o === "rede" || f === "rede";
}

export const ROTULO_REDE = "Rede Everlog";
export const TITULO_SELO_REDE = "Rede Everlog: cobertura declarada pela transportadora, sem histórico de CT-e com você";

// Grupos da lista: a carteira primeiro, depois quem atende o destino (CT-e) e,
// por fim, a Rede Everlog (cobertura declarada).
export function agruparCandidatos(candidatos) {
  const lista = candidatos || [];
  return [
    { chave: "carteira", titulo: "Sua carteira", itens: lista.filter((c) => c.naCarteira) },
    { chave: "destino", titulo: "Outras que atendem o destino", itens: lista.filter((c) => !c.naCarteira && !ehCandidatoRede(c)) },
    { chave: "rede", titulo: "Rede Everlog · cobertura declarada", itens: lista.filter((c) => !c.naCarteira && ehCandidatoRede(c)) },
  ].filter((g) => g.itens.length);
}

export function rotuloDuracao(min) {
  const n = Number(min);
  if (n >= 60 && n % 60 === 0) return `${n / 60} h`;
  return `${n} min`;
}

// Hora prevista de fechamento se publicar agora (só para orientar).
export function previsaoFechamento(duracaoMin, agora) {
  if (!duracaoValida(duracaoMin)) return null;
  const base = agora != null ? agora : Date.now();
  return new Date(base + Number(duracaoMin) * 60000);
}

// ── Novo leilão a partir da tela de Leilões (13/10/2026) ────────────────────

function baseDaCotacao(emb) {
  return emb ? emb.baseNumEmbarque || emb.numEmbarque : null;
}

// Cotações que podem ir a leilão agora: Pendente/Stand-by na versão corrente e
// sem leilão vivo (porBase vem de GET /gobid/leiloes/ativos). Mais novas antes.
export function cotacoesLeiloaveis(embarques, porBase) {
  const ativos = porBase || {};
  return (embarques || [])
    .filter((e) => podeLeiloar(e) && !ativos[baseDaCotacao(e)])
    .sort((a, b) => ts(b.dataEmbarque) - ts(a.dataEmbarque));
}

export function buscaCotacao(emb, termo) {
  if (!termo) return true;
  const alvo = semAcento(termo).trim();
  if (!alvo) return true;
  const campos = [
    emb.numeroCotacao != null ? String(emb.numeroCotacao) : null,
    emb.numeroCotacao != null ? `nº ${emb.numeroCotacao}` : null,
    baseDaCotacao(emb),
    emb.codigoPedido,
    emb.cliente,
    emb.cidadeDestino,
    emb.ufDestino,
    emb.transportadora,
  ];
  return campos.some((c) => c != null && semAcento(c).indexOf(alvo) !== -1);
}

// ── Preço-alvo: nunca pré-preenchido (decisão 13/10/2026) ────────────────────
// O frete atual ou a simulação podem vir de uma tabela errada, e o alvo é
// visível e aceitável com um clique. Só mostramos a referência; usar é
// decisão explícita do usuário.
export function referenciaAlvo(embarque, cotacoes) {
  if (embarque && embarque.valor_total > 0) {
    return { valor: embarque.valor_total, origem: "frete", transportadora: embarque.transportadora || null };
  }
  const validas = (cotacoes || []).filter((c) => c && Number(c.valor) > 0);
  if (!validas.length) return null;
  const menor = validas.reduce((a, b) => (Number(b.valor) < Number(a.valor) ? b : a));
  return { valor: Number(menor.valor), origem: "simulacao", transportadora: menor.transportadora || null };
}
